/** Roles that bypass license expiry (admin tooling). */
const LICENSE_EXEMPT_ROLES = new Set(['admin', 'superadmin', 'helpdesk'])

/** Ignore corrupt/legacy timestamps before the product existed. */
const MIN_ACTIVATION_MS = new Date('2018-01-01T00:00:00.000Z').getTime()

/** Allow small future-dated activations (test users, clock skew). */
const FUTURE_ACTIVATION_GRACE_MS = 24 * 60 * 60 * 1000

/**
 * @param {unknown} activated Firestore Timestamp, ms number, Date, or date string
 * @returns {number|null}
 */
export function activatedToMillis(activated) {
  if (activated == null || activated === '') return null

  if (typeof activated === 'number' && Number.isFinite(activated)) {
    return activated
  }

  if (typeof activated === 'object') {
    if (typeof activated.toDate === 'function') {
      const ms = activated.toDate().getTime()
      return Number.isNaN(ms) ? null : ms
    }
    if (typeof activated.seconds === 'number') {
      return activated.seconds * 1000
    }
  }

  if (activated instanceof Date) {
    const ms = activated.getTime()
    return Number.isNaN(ms) ? null : ms
  }

  const parsed = new Date(activated).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * @param {unknown} activated
 * @param {Date} [now]
 */
export function hasValidActivationDate(activated, now = new Date()) {
  const ms = activatedToMillis(activated)
  if (ms == null) return false
  if (ms < MIN_ACTIVATION_MS) return false
  if (ms > now.getTime() + FUTURE_ACTIVATION_GRACE_MS) return false
  return true
}

/**
 * @param {unknown} activated
 * @returns {Date|null}
 */
export function getLicenseExpiryDate(activated) {
  const ms = activatedToMillis(activated)
  if (ms == null) return null

  const expiry = new Date(ms)
  expiry.setFullYear(expiry.getFullYear() + 1)
  return expiry
}

/**
 * @param {unknown} activated
 * @param {Date} [now]
 */
export function isLicenseExpired(activated, now = new Date()) {
  if (!hasValidActivationDate(activated, now)) return false

  const expiry = getLicenseExpiryDate(activated)
  if (!expiry) return false
  return now.getTime() >= expiry.getTime()
}

/**
 * All gates required before persisting active: null (login + nightly job).
 * Router uses resolveEffectiveActive only — redirect, no Firestore write.
 * @param {Record<string, unknown>|null|undefined} user
 * @param {Date} [now]
 */
export function shouldDeactivateExpiredLicense(user, now = new Date()) {
  if (!user || user.active !== true) return false
  if (isLicenseExemptRole(/** @type {string[]} */ (user.roles))) return false
  return isLicenseExpired(user.activated, now)
}

/**
 * @param {string[]|undefined|null} roles
 */
export function isLicenseExemptRole(roles) {
  if (!Array.isArray(roles)) return false
  return roles.some(
    (role) =>
      typeof role === 'string' &&
      LICENSE_EXEMPT_ROLES.has(role.trim().toLowerCase())
  )
}

/**
 * Apply license expiry to a Firestore user payload.
 * @returns {boolean|null} effective active flag for routing/UI
 */
export function resolveEffectiveActive(user, now = new Date()) {
  if (!user || isLicenseExemptRole(user.roles)) {
    return user?.active ?? null
  }

  if (!isLicenseExpired(user.activated, now)) {
    return user.active ?? null
  }

  return null
}
